import React, { useEffect, useState } from 'react';
import api from '../lib/api';
import { useLang } from '../i18n/translations';
import { Sun, CloudSun, Cloud, CloudFog, CloudDrizzle, CloudRain, CloudSnow, CloudLightning, Wind, Droplets } from 'lucide-react';

export const codeInfo = (code) => {
  if (code === 0) return { icon: Sun, key: 'wClear' };
  if (code <= 2) return { icon: CloudSun, key: 'wPartly' };
  if (code === 3) return { icon: Cloud, key: 'wCloudy' };
  if (code <= 48) return { icon: CloudFog, key: 'wFog' };
  if (code <= 57) return { icon: CloudDrizzle, key: 'wDrizzle' };
  if (code <= 67) return { icon: CloudRain, key: 'wRain' };
  if (code <= 77) return { icon: CloudSnow, key: 'wSnow' };
  if (code <= 82) return { icon: CloudRain, key: 'wShowers' };
  if (code <= 86) return { icon: CloudSnow, key: 'wSnow' };
  return { icon: CloudLightning, key: 'wThunder' };
};

const LOCALES = { bs: 'hr-HR', en: 'en-GB', de: 'de-DE', tr: 'tr-TR', ar: 'ar' };

export const WeatherWidget = ({ lat, lng, city }) => {
  const { t, lang } = useLang();
  const [w, setW] = useState(null);

  useEffect(() => {
    let active = true;
    setW(null);
    api.get(`/weather?lat=${lat}&lng=${lng}`)
      .then(({ data }) => { if (active) setW(data); })
      .catch(() => {});
    return () => { active = false; };
  }, [lat, lng]);

  if (!w?.current) return null;
  const cur = codeInfo(w.current.weather_code);
  const CurIcon = cur.icon;

  return (
    <div className="bg-white rounded-xl border border-slate-100 p-6" data-testid="weather-widget">
      <h3 className="font-bold text-[#0b1a3d] mb-1">{t('weather')}</h3>
      <p className="text-xs text-slate-400 mb-4">{city}</p>
      <div className="flex items-center gap-4">
        <CurIcon className="h-12 w-12 text-[#001752]" strokeWidth={1.5} />
        <div>
          <p className="text-3xl font-bold text-[#0b1a3d]" data-testid="weather-temp">{Math.round(w.current.temperature_2m)}°C</p>
          <p className="text-sm text-slate-500">{t(cur.key)}</p>
        </div>
      </div>
      <div className="flex flex-wrap gap-4 mt-3 text-xs text-slate-500">
        <span className="flex items-center gap-1"><Wind className="h-3.5 w-3.5" /> {t('wWind')}: {Math.round(w.current.wind_speed_10m)} km/h</span>
        <span className="flex items-center gap-1"><Droplets className="h-3.5 w-3.5" /> {t('wHumidity')}: {w.current.relative_humidity_2m}%</span>
      </div>
      {w.daily?.time?.length > 3 && (
        <div className="grid grid-cols-3 gap-2 mt-5 pt-4 border-t border-slate-100">
          {w.daily.time.slice(1, 4).map((d, i) => {
            const info = codeInfo(w.daily.weather_code[i + 1]);
            const DayIcon = info.icon;
            return (
              <div key={d} className="text-center" data-testid={`weather-day-${i}`}>
                <p className="text-xs font-semibold text-slate-600 capitalize">
                  {new Date(d).toLocaleDateString(LOCALES[lang] || 'hr-HR', { weekday: 'short' })}
                </p>
                <DayIcon className="h-6 w-6 mx-auto my-1.5 text-[#001752]" strokeWidth={1.5} />
                <p className="text-xs text-slate-600">
                  <b>{Math.round(w.daily.temperature_2m_max[i + 1])}°</b> / {Math.round(w.daily.temperature_2m_min[i + 1])}°
                </p>
              </div>
            );
          })}
        </div>
      )}
      <p className="text-[10px] text-slate-300 mt-3">Weather data by Open-Meteo.com</p>
    </div>
  );
};
